import React, { useEffect, useState } from "react";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import NestedItemSelect, {
  Section,
} from "@components/NestedItemSelect/NestedItemSelect";
import "./style.css";
import useGetProject from "./api/useGetProject";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { Project } from "./types/Project";
import { TextField } from "@components/FormsV2";
import { useV2GetAccountQuery, useV2CreateBlueprintMutation } from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { withErrorReason } from "@components/CatalogIndex/saveErrorToast";
import {
  buildGeneralSection,
  buildPsSection,
  buildMsSection,
  buildExpenseSection,
  buildGovernanceSection,
  buildProductSection,
  buildResourcesSection,
  buildPayload,
} from "./projectSections";

const GENERAL_OPTIONS = [
  "executive-summary",
  "solution-summary",
  "rate-table",
  "payment-term",
];

const buildSections = (project: Project | null): Section[] => [
  buildGeneralSection(GENERAL_OPTIONS),
  buildPsSection(project),
  buildMsSection(project),
  buildExpenseSection(project),
  buildGovernanceSection(project),
  buildProductSection(project),
  buildResourcesSection(project),
];

interface Props {
  projectId: string;
  show: boolean;
  onClose: () => void;
}

const CreateBlueprintModal: React.FC<Props> = ({
  projectId,
  show,
  onClose,
}) => {
  // isFetching, not isLoading: a reopen refetches, and the sections must be
  // built from that response rather than the cached project.
  const { project, isFetching: isLoading } = useGetProject(projectId);
  const { data: accountData } = useV2GetAccountQuery({});
  const [createBlueprint, { isLoading: isSubmitting }] =
    useV2CreateBlueprintMutation();

  const [blueprintName, setBlueprintName] = useState<string>("");
  const [sections, setSections] = useState<Section[]>(() =>
    buildSections(project || null)
  );
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] = useState<"success" | "danger">(
    "success"
  );

  // Runs on mount too, so a modal that mounts already open (show=true) with
  // the project cached still seeds its sections.
  const hasProject = !!project;
  useEffect(() => {
    if (show && !isLoading && project) setSections(buildSections(project));
  }, [show, isLoading, hasProject]);

  // The blueprint's account relationship needs the numeric account-id, not
  // the JSON:API resource id.
  const { "account-id": numericAccountId } =
    accountData?.data?.attributes ?? {};
  const accountId =
    numericAccountId != null
      ? String(numericAccountId)
      : accountData?.data?.id ?? "";

  const handleSubmit = () => {
    if (!blueprintName.trim()) {
      setAlertVariant("danger");
      setAlertMessage("Blueprint name is required.");
      return;
    }

    const payload = buildPayload(sections, project);

    createBlueprint({
      body: {
        data: {
          type: "blueprints",
          attributes: {
            name: blueprintName.trim(),
            payload,
          },
          relationships: {
            account: {
              data: { id: accountId, type: "accounts" },
            },
          },
        },
      },
    })
      .unwrap()
      .then(() => {
        setAlertVariant("success");
        setAlertMessage("Blueprint created successfully.");
        setTimeout(() => {
          setAlertMessage(null);
          onClose();
        }, 1500);
      })
      .catch((err) => {
        setAlertVariant("danger");
        setAlertMessage(
          withErrorReason("Something went wrong creating the blueprint.", err)
        );
      });
  };

  return (
    <>
      {alertMessage && (
        <ToastAlert
          variant={alertVariant}
          text={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
      <ScopeStackModal
        className="createBlueprintFromProjectModal"
        show={show}
        modalTitle="Create Blueprint from Project"
        modalBody={
          isLoading ? (
            <ScopeStackSpinner />
          ) : (
            <>
              <div className="fields">
                <TextField
                  id="name"
                  label="Blueprint Name"
                  placeholder="Enter Blueprint Name"
                  onChange={(e) => setBlueprintName(e.target.value)}
                />
              </div>
              <div className="context">Customize Blueprint</div>
              <div className="description">
                Select the items you wish to include on this blueprint.
              </div>
              <NestedItemSelect
                sections={sections}
                onSectionsChange={setSections}
              />
            </>
          )
        }
        button1Text="Cancel"
        button2Text={isSubmitting ? "Creating..." : "Create Blueprint"}
        handleClose={onClose}
        handleButton1Click={onClose}
        handleButton2Click={handleSubmit}
        button2Disabled={isSubmitting || !accountId}
      />
    </>
  );
};

export default CreateBlueprintModal;
